/**
 * Fetch Unlocked Videos Page UseCase
 *
 * Pages through the locally-stored unlocked-content records, fetches the
 * matching media documents from Firestore, applies blocked / hidden /
 * usersBlockedMe filtering, and preserves the original path order.
 *
 * Flutter parity:
 *  - lib/presentation/screens/content/unlocked_content_screen.dart:109-155
 *    (pagination shape, re-ordering against `valuesToSearch`)
 *  - lib/data/data_sources/content_data_source.dart:362-400
 *    (post-query filters: blocked, usersBlockedMe, hiddenContent)
 */

import { userMediaService } from '@/domain/services/userMedia/userMediaService';
import { shouldFilterOutBlockedOrHiddenMediaUseCase } from '@/domain/usecases/userMedia/shouldFilterOutBlockedOrHiddenMediaUseCase';
import { SERVER_FETCH_CONSTANTS } from '@/domain/constants/serverFetchConstants';
import type { UnlockedContentStorage } from '@/domain/entities/secureStorage/secureStorageEntities';
import type { UserMediaEntity } from '@/domain/entities/userMedia/userMediaEntity';
import type { UserEntity } from '@/domain/entities/user/userEntity';

export interface FetchUnlockedVideosPageParams {
  localItems: UnlockedContentStorage[];
  pageIndex: number;
  pageSize?: number;
  currentUser: UserEntity | null;
}

export interface FetchUnlockedVideosPageResult {
  medias: UserMediaEntity[];
  hasMore: boolean;
}

export class FetchUnlockedVideosPageUseCase {
  /**
   * Execute one page fetch.
   *
   * Steps (mirroring Flutter `unlocked_content_screen.dart:_getData`):
   *  1. Slice `localItems` by [pageIndex * pageSize, pageIndex * pageSize + pageSize).
   *  2. Fetch the matching media documents (Firestore `in` query — order is not
   *     guaranteed by Firestore).
   *  3. Filter out blocked/hidden/usersBlockedMe medias (parity with
   *     content_data_source.dart:375-393).
   *  4. Re-order the surviving medias by iterating `pathsInSlice` outer, then
   *     matching `media.relativePath === path` inner — required for parity
   *     with unlocked_content_screen.dart:142-148, since Firestore `in`
   *     queries don't honour input order.
   *  5. Compute `hasMore` from the slice bounds against the local list length.
   *
   * Errors from the service are not caught here — the hook layer is
   * responsible for logging via appLogger.error.
   */
  async execute(
    params: FetchUnlockedVideosPageParams,
  ): Promise<FetchUnlockedVideosPageResult> {
    const {
      localItems,
      pageIndex,
      pageSize = SERVER_FETCH_CONSTANTS.DEFAULT_LIMIT,
      currentUser,
    } = params;

    const start = pageIndex * pageSize;
    if (start >= localItems.length) {
      return { medias: [], hasMore: false };
    }

    const end = Math.min(start + pageSize, localItems.length);
    const pathsInSlice = localItems.slice(start, end).map((item) => item.path);

    if (pathsInSlice.length === 0) {
      return { medias: [], hasMore: false };
    }

    const rawMedias = await userMediaService.getMediasByPaths(pathsInSlice);

    const surviving = rawMedias.filter(
      (media) => !shouldFilterOutBlockedOrHiddenMediaUseCase.execute(media, currentUser),
    );

    // Re-order surviving medias to match `pathsInSlice` order — Flutter
    // unlocked_content_screen.dart:142-148.
    const ordered: UserMediaEntity[] = [];
    for (const path of pathsInSlice) {
      const match = surviving.find((media) => media.relativePath === path);
      if (match) {
        ordered.push(match);
      }
    }

    const hasMore = end < localItems.length;

    return { medias: ordered, hasMore };
  }
}

export const fetchUnlockedVideosPageUseCase = new FetchUnlockedVideosPageUseCase();
